import { useFieldBinding } from '@/hooks/use-field-binding';
import { AddressFields, useGoogleAddressValidation } from '@/hooks/use-google-address-validation';
import useModal from '@/hooks/use-modals';
import { AltAddress, Option } from '@/schemas/sceneDataSchema';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import { Autocomplete, Box, FormControl, Stack, TextField } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AddressValidationModal } from '../../AddressValidationModal';

function asString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  return '';
}

const filterOptions = createFilterOptions<Option>({
  matchFrom: 'any',
  stringify: (option) => option.label,
});

// Replaces default MUI sorting to order startsWith before contains
// ex. if you type "ka" then "Kansas" will appear before "Alaska"
const customFilterOptions = (options: Option[], params: Parameters<typeof filterOptions>[1]) => {
  const filtered = filterOptions(options, params);

  if (!params.inputValue) return filtered;

  const inputValue = params.inputValue.toLowerCase();

  return filtered.sort((a, b) => {
    const aStartsWith = a.label.toLowerCase().startsWith(inputValue);
    const bStartsWith = b.label.toLowerCase().startsWith(inputValue);

    if (aStartsWith && !bStartsWith) return -1;
    if (!aStartsWith && bStartsWith) return 1;
    return 0;
  });
};

export function AltAddressFormElement({ form, component }: { form: UseFormReturn; component: AltAddress }) {
  const { getModal } = useModal();
  const { validateAddress, isValidating } = useGoogleAddressValidation();

  const fieldKeys = useMemo(
    () => ({
      address1: encodeFieldKey(component.address1),
      address2: encodeFieldKey(component.address2),
      city: encodeFieldKey(component.city),
      state: encodeFieldKey(component.state),
      zip: encodeFieldKey(component.zip),
    }),
    [component.address1, component.address2, component.city, component.state, component.zip]
  );

  const bindings = {
    address1: useFieldBinding(component.address1, form),
    address2: useFieldBinding(component.address2, form),
    city: useFieldBinding(component.city, form),
    state: useFieldBinding(component.state, form),
    zip: useFieldBinding(component.zip, form),
  };

  const [suggestedAddress, setSuggestedAddress] = useState<AddressFields | null>(null);
  const lastValidatedValues = useRef<AddressFields | null>(null);

  const getCurrentValues = (): AddressFields => ({
    address1: asString(form.getValues(fieldKeys.address1)),
    city: asString(form.getValues(fieldKeys.city)),
    state: asString(form.getValues(fieldKeys.state)),
    zip: asString(form.getValues(fieldKeys.zip)),
  });

  // Open modal after suggestedAddress is set and modal is mounted
  useEffect(() => {
    if (suggestedAddress) {
      getModal(component.validation_modal_id)?.open();
    }
  }, [suggestedAddress, getModal, component.validation_modal_id]);

  const valuesChanged = (values: AddressFields): boolean => {
    if (!lastValidatedValues.current) return true;

    return (
      lastValidatedValues.current.address1 !== values.address1 ||
      lastValidatedValues.current.city !== values.city ||
      lastValidatedValues.current.state !== values.state ||
      lastValidatedValues.current.zip !== values.zip
    );
  };

  const shouldValidate = (currentValues: AddressFields): boolean => {
    const allFieldsFilled = Boolean(
      currentValues.address1 && currentValues.city && currentValues.state && currentValues.zip
    );
    return allFieldsFilled && !isValidating && valuesChanged(currentValues);
  };

  const checkAndValidateAddress = (): void => {
    const currentValues = getCurrentValues();

    if (shouldValidate(currentValues)) {
      void validateAddress(currentValues).then((result) => {
        lastValidatedValues.current = { ...currentValues };
        if (result.suggestedAddress) {
          setSuggestedAddress(result.suggestedAddress);
        }
      });
    }
  };

  const handleSelectAddress = (address: AddressFields): void => {
    const fields = ['address1', 'city', 'state', 'zip'] as const;

    fields.forEach((field) => {
      form.setValue(fieldKeys[field], address[field]);
      bindings[field].sync(address[field]);
    });

    form.trigger([fieldKeys.address1, fieldKeys.city, fieldKeys.state, fieldKeys.zip]).catch(() => {});
    lastValidatedValues.current = { ...address };
    getModal(component.validation_modal_id)?.close();
    setSuggestedAddress(null);
  };

  const renderTextField = (
    fieldName: 'address1' | 'address2' | 'city' | 'zip',
    config: { label: string | null; description: string | null },
    defaultLabel: string,
    includeValidation = true
  ) => (
    <Controller
      control={form.control}
      name={fieldKeys[fieldName]}
      render={({ field, fieldState: { error } }) => (
        <FormControl
          fullWidth
          error={!!error}
        >
          <TextField
            type='text'
            label={config.label ?? defaultLabel}
            variant='outlined'
            fullWidth
            role='textbox'
            error={!!error}
            placeholder={config.label ?? defaultLabel}
            helperText={error ? error.message : config.description}
            {...field}
            value={(field.value as string) ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value);
              bindings[fieldName].sync(e.target.value);
            }}
            onBlur={() => {
              field.onBlur();
              void bindings[fieldName].onBlur();
              if (includeValidation) {
                void checkAndValidateAddress();
              }
            }}
          />
        </FormControl>
      )}
    />
  );

  const renderStateDropdown = () => (
    <Controller
      control={form.control}
      name={fieldKeys.state}
      render={({ field, fieldState: { error } }) => {
        const handleStateChange = (_: unknown, newValue: Option | null) => {
          if (newValue?.value) {
            field.onChange(newValue.value);
            bindings.state.sync(newValue.value.toString());
            void bindings.state.onBlur();
            void checkAndValidateAddress();
          } else {
            field.onChange('');
            bindings.state.sync('');
          }
        };

        return (
          <FormControl
            fullWidth
            error={!!error}
          >
            <Autocomplete<Option>
              options={component.state.options}
              getOptionLabel={(option) => option?.label ?? ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              value={component.state.options.find((option) => option.value.toString() === field.value) ?? null}
              onChange={handleStateChange}
              onBlur={field.onBlur}
              filterOptions={customFilterOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={component.state.label ?? 'State'}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
              aria-label={component.state.label ?? 'State'}
              clearOnEscape
              autoHighlight
            />
          </FormControl>
        );
      }}
    />
  );

  return (
    <>
      <Stack spacing={2}>
        {renderTextField('address1', component.address1, 'Address 1')}
        {renderTextField('address2', component.address2, 'Address 2', false)}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' },
            gap: 2,
          }}
        >
          {renderTextField('city', component.city, 'City')}
          {renderStateDropdown()}
          {renderTextField('zip', component.zip, 'Zip Code')}
        </Box>
      </Stack>

      <AddressValidationModal
        id={component.validation_modal_id}
        providedAddress={getCurrentValues()}
        suggestedAddress={suggestedAddress ?? { address1: '', city: '', state: '', zip: '' }}
        onSelectAddress={handleSelectAddress}
      />
    </>
  );
}
